/* @jsx h */
import { defineComponent, h, onMounted } from "vue";
import { formatPrice, round } from "@lemmonhq/orders";
import { PaymentDetails } from "@lemmonhq/types";
import { Ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { calculatePaidTotal } from "./paymentUtils";

export type SplitPaymentContentProps = {
  remainingTotal: number;
  donePayments: PaymentDetails[];
  splitAmount: Ref<number>;
  splitCustomAmount: Ref<number>;
  splitCount: Ref<number>;
  subtotal: Ref<number>;
  activeSplitTab: Ref<string>;
};
const props = [
  "remainingTotal",
  "donePayments",
  "splitAmount",
  "splitCustomAmount",
  "splitCount",
  "activeSplitTab",
  "subtotal",
];

export const SplitPaymentEqual = defineComponent<SplitPaymentContentProps>({
  name: "SplitPaymentEqual",
  props: [...props],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    // Equal shares split what earlier payments left over. Earlier equal
    // shares stay out of that base (each is a share of the same base). Custom
    // splits, full/partial payments and table credit come off it; credit is
    // valued by `subtotal` (its `amount` is 0: the credit is a tender, not
    // money taken).
    const calculateSplitAmount = (
      subtotal: number,
      donePayments: PaymentDetails[],
      splitCount: number,
    ) => {
      const total =
        subtotal -
        donePayments
          // Legacy split rows without a type were never subtracted; keep that.
          .filter(
            (el) =>
              !el.split_payment ||
              el.split_payment.split_type == "custom" ||
              el.split_payment.split_type == "table_credit",
          )
          .reduce((s, el) => {
            if (el.split_payment?.split_type == "custom") return s + el.amount;
            return s + calculatePaidTotal([el]);
          }, 0);
      return total / splitCount;
    };
    const updateSplitCount = (increment: boolean) => {
      const { splitCount } = props;
      const newValue = increment ? splitCount.value + 1 : splitCount.value - 1;
      if (newValue >= 2) {
        splitCount.value = newValue;
        props.splitAmount.value = calculateSplitAmount(
          props.subtotal.value,
          props.donePayments,
          splitCount.value,
        );
      }
    };
    onMounted(() => {
      if (props.splitCount.value) {
        props.splitAmount.value = calculateSplitAmount(
          props.subtotal.value,
          props.donePayments,
          props.splitCount.value,
        );
      }
    });
    return () => {
      const { splitCount } = props;
      const perPersonAmount =
        splitCount.value >= 2
          ? formatPrice(
              calculateSplitAmount(
                props.subtotal.value,
                props.donePayments,
                splitCount.value,
              ),
              true,
              2,
              auth.user.value?.restaurant.currency || "CHF",
            )
          : null;
      return (
        <div class="space-y-6 w-full max-w-xs">
          <div class="flex items-center justify-center gap-4">
            <button
              class="btn btn-circle btn-secondary"
              onClick={() => updateSplitCount(false)}
              disabled={splitCount.value <= 2}
            >
              <span class="material-symbols-outlined">remove</span>
            </button>
            <div class="w-24 text-center">
              <div class="text-2xl font text-accent">{splitCount.value}</div>
              <div class="text-xs text-base-content/70">
                {t("tables.checkout.people")}
              </div>
            </div>
            <button
              class="btn btn-circle btn-secondary"
              onClick={() => updateSplitCount(true)}
              disabled={props.remainingTotal === 0}
            >
              <span class="material-symbols-outlined">add</span>
            </button>
          </div>
          <div class="divider"></div>

          {perPersonAmount && (
            <div class="text-center space-y-1">
              <div class="text-2xl  text-accent">{perPersonAmount}</div>
              <div class="text-xs text-base-content/70">
                {t("tables.checkout.per_person")}
              </div>
            </div>
          )}
        </div>
      );
    };
  },
});
export const SplitPaymentCustom = defineComponent<SplitPaymentContentProps>({
  name: "SplitPaymentEqual",
  props: [...props],
  setup(props) {
    const auth = useAuth();
    onMounted(() => {
      props.splitAmount.value = 0;
    });
    return () => {
      const { t } = useLocalization();
      const { splitAmount } = props;
      return (
        <div class="space-y-6  ">
          <div class="form-control  justify-center items-center ">
            <label class="label">
              <span class="label-text">
                {t("tables.checkout.enter_split_amount")}
              </span>
            </label>
            <input
              type="number"
              disabled={props.remainingTotal === 0}
              value={props.splitCustomAmount}
              step="0.01"
              max={props.remainingTotal}
              placeholder="0.00"
              class="w-32 input input-bordered  text-center "
              onInput={(e) => {
                const num = Number((e.target as HTMLInputElement).value);
                console.log("num", {
                  num,
                  value: (e.target as any)?.value,
                  t: e.target,
                });
                if (num && Number.isFinite(num)) {
                  if (round(num) > round(props.remainingTotal)) {
                    alert(
                      `value can't exceed ${formatPrice(
                        props.remainingTotal,
                        true,
                        2,
                        auth.user.value?.restaurant.currency || "CHF",
                      )}`,
                    );
                  } else {
                    props.splitCustomAmount.value = num;
                    splitAmount.value = num;
                  }
                }
              }}
            />
            <div class="text-xs py-1">
              {formatPrice(
                props.remainingTotal,
                true,
                2,
                auth.user.value?.restaurant.currency || "CHF",
              )}{" "}
              {t("tables.checkout.remaining")}
            </div>
          </div>
        </div>
      );
    };
  },
});

export const SplitPaymentContent = defineComponent<SplitPaymentContentProps>({
  name: "SplitPaymentContent",
  props: [...props],
  setup(props) {
    const activeTab = props.activeSplitTab;
    const { t } = useLocalization();
    return () => {
      return (
        <div class="p-1 lg:p-4 space-y-6   ">
          <h1 class="text-center hidden text-2xl text-accent font-normal">
            {t("tables.checkout.choose_method")}
          </h1>
          {/* Tabs */}
          <div class="flex flex-col sm:flex-row  pb-4 rounded-none  p-1 justify-around items-center">
            <a
              class={`btn flex-1 ${
                activeTab.value === "equal" ? "btn-primary" : ""
              } gap-2`}
              onClick={() => (activeTab.value = "equal")}
            >
              <span class="material-symbols-outlined">group</span>
              {t("tables.checkout.split_equally_button")}
            </a>
            <div class="flex-1 px-4 divider  text-accent">
              {t("tables.checkout.or")}
            </div>
            <a
              class={`btn flex-1 ${
                activeTab.value === "custom" ? "btn-primary" : ""
              } gap-2`}
              onClick={() => (activeTab.value = "custom")}
            >
              <span class="material-symbols-outlined">payments</span>
              {t("tables.checkout.custom_amount_button")}
            </a>
          </div>

          <div class="card ">
            <div class="card-body items-center text-center">
              {/* Equal Split Content */}
              {activeTab.value === "equal" && <SplitPaymentEqual {...props} />}

              {/* Custom Amount Content */}
              {activeTab.value === "custom" && (
                <SplitPaymentCustom {...props} />
              )}

              {/* Summary */}
            </div>
          </div>
        </div>
      );
    };
  },
});
